// libs
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// components
import ToastWithLogo from 'shared/ui-components/ToastWithLogo';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import useDeeplinkToast from './useDeeplinkToast';

// selectors
import { getStoreId } from 'shared/store/app/selectors';

// constants
import { WISHLIST, BASE_CART_URL, MY_ACCOUNT } from 'shared/constants/uri';
import { WEB_APP_TYPE } from 'shared/constants/index';

// helpers
import { getWebAppType } from 'shared/helpers/deviceType/webAppType';

const STORE_SWITCH_FLAG = 'store_switched_via_toggle';
const LAST_STORE_KEY = 'last_visited_store_id';
const COACHMARK_SHOWN_KEY = 'store_toggle_coachmark_shown';
export const CART_REFERRER_STORE_KEY = 'cart_referrer_store';
const TOAST_OCCURRENCE_KEY = 'store_switch_toast_count';

const EXCLUDED_PATHS = [WISHLIST, BASE_CART_URL, MY_ACCOUNT];

function StoreSwitchToast() {
  const currentStoreId = useSelector(getStoreId);
  const storeSwitchToastConfig = useRemoteConfig('storeSwitchToast');
  const { showDeeplinkToast, deeplinkToastConfig, dismissDeeplinkToast } = useDeeplinkToast();
  const [showToast, setShowToast] = useState(false);
  const { pathname } = useLocation();

  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  const isExcludedPage =
    isInIframe || EXCLUDED_PATHS.some((path) => pathname.startsWith(path));

  useEffect(() => {
    // Get the last visited store from localStorage
    const lastStoreId = localStorage.getItem(LAST_STORE_KEY);

    // Check if store was switched via toggle - if so, don't show this toast
    const switchedViaToggle = sessionStorage.getItem(STORE_SWITCH_FLAG);

    // On desktop, if the coachmark is being shown, don't show the toast
    const isDesktop = getWebAppType() === WEB_APP_TYPE.DESKTOP;
    const coachmarkShown = sessionStorage.getItem(COACHMARK_SHOWN_KEY);

    // Save referrer store when entering cart/wishlist so CartNoItems can navigate back
    if (isExcludedPage && lastStoreId) {
      localStorage.setItem(CART_REFERRER_STORE_KEY, lastStoreId);
    }

    // Update the last visited store only on non-excluded pages (cart/wishlist/account)
    // so that returning to the same store after cart doesn't trigger the toast
    if (!isExcludedPage) {
      localStorage.setItem(LAST_STORE_KEY, currentStoreId);
    }

    if (isDesktop && coachmarkShown) {
      sessionStorage.removeItem(COACHMARK_SHOWN_KEY);
      return;
    }

    // If store changed AND it was NOT via toggle to current store, show the toast
    // switchedViaToggle should match currentStoreId if we just toggled to this store
    if (lastStoreId && lastStoreId !== currentStoreId && switchedViaToggle !== currentStoreId) {
      if (storeSwitchToastConfig && !isExcludedPage) {
        const { maxOccurrence = 0 } = storeSwitchToastConfig;

        // maxOccurrence: 0 means unlimited
        if (maxOccurrence > 0) {
          const occurrenceCount = Number(localStorage.getItem(TOAST_OCCURRENCE_KEY) || 0);
          if (occurrenceCount >= maxOccurrence) {
            return;
          }
          localStorage.setItem(TOAST_OCCURRENCE_KEY, String(occurrenceCount + 1));
        }

        setShowToast(true);
      }
    }
  }, [currentStoreId, storeSwitchToastConfig, isExcludedPage]);

  const handleClose = () => {
    setShowToast(false);
    dismissDeeplinkToast();
  };

  const isDeeplinkToastActive = showDeeplinkToast && !showToast;
  const activeConfig = isDeeplinkToastActive ? deeplinkToastConfig : storeSwitchToastConfig;
  const isVisible = showToast || showDeeplinkToast;

  if (!activeConfig) {
    return null;
  }

  return (
    <ToastWithLogo
      message={activeConfig.message}
      logoUrl={activeConfig.logoUrl}
      actionText={activeConfig.actionText}
      autoHideDuration={activeConfig.autoHideDuration}
      onActionClick={handleClose}
      show={isVisible}
      onHide={handleClose}
    />
  );
}

export default StoreSwitchToast;